import _ from 'lodash'
import { Component, ControllerConnectionItem, Data, Logger, Structure } from '@wix/thunderbolt-becky-types'
import { AppWidgetsGhostsFunctionLibrary } from '../../types/byRef'
import { buildWidgetUrl } from '../byRefFunctionLibrary'
import { getRefCompIdFromInflatedId } from '../utils'

const APP_WIDGET_GHOST_VALUE = 'APP_WIDGET_GHOST'

const getAppControllerId = (structure: Structure, widgetCompId: string): string => structure[widgetCompId].dataQuery!

const getControllerConnections = (data: Data, controllerId: string): { [role: string]: string } => {
	return Object.assign(
		{},
		..._.values(data.connections_data).map((connection) => {
			const connectionItem = connection.items.find(
				(item) => (item as ControllerConnectionItem).controllerId === controllerId
			)
			if (connectionItem) {
				return {
					[connectionItem.role]: connection.id,
				}
			}
			return {}
		})
	)
}

const getStructureForConnection = (structure: Structure, connectionId: string): Component =>
	_.values(structure).find((compStructure) => compStructure.connectionQuery === connectionId) ||
	structure[getRefCompIdFromInflatedId(connectionId)] // inflated ComponentRef id

const getComponentRole = (structure: Structure, data: Data, controllerId: string, compId: string): string => {
	const connectionId = structure[compId].connectionQuery
	const item = data.connections_data[connectionId].items.find(
		(connectionItem) => (connectionItem as ControllerConnectionItem).controllerId === controllerId
	)
	return item!.role
}

/**
 * wrap structure tree with an AppWidget component, assigning a controller and connections based on existing WixCode
 * connection roles to newly added AppWidget
 *
 * this is required to support legacy wix blocks widgets which did not have an encasing AppWidget component as part
 * of their template.
 */
const wrapTemplateWithAppWidget: AppWidgetsGhostsFunctionLibrary['wrapTemplateWithAppWidget'] = (
	structure,
	data,
	rootId
) => {
	const appController = {
		type: 'AppController',
		id: 'syntheticAppController',
	}

	const appWidgetStructure = {
		type: 'Container',
		id: 'syntheticAppWidget',
		components: [rootId],
		dataQuery: appController.id,
		componentType: 'AppWidget',
	}

	// make the root component a child of our synthetic AppWidget
	const updatedRootStructure = {
		...structure[rootId],
		parent: appWidgetStructure.id,
	}

	// use existing wixCode connection items roles as our app controller connections
	const connections_data: Data['connections_data'] = _.mapValues(data.connections_data, (connectionData) => {
		const items = []
		const connectionItem = connectionData.items[0]
		if (connectionItem) {
			items.push({
				type: 'ConnectionItem',
				role: connectionItem.role,
				controllerId: appController.id,
				isPrimary: true,
			} as any)
		}
		return {
			...connectionData,
			items,
		}
	})

	return {
		structure: {
			...structure,
			[updatedRootStructure.id]: updatedRootStructure,
			[appWidgetStructure.id]: appWidgetStructure,
		} as Structure,
		data: {
			...data,
			connections_data,
		} as Data,
		widgetCompId: appWidgetStructure.id,
	}
}

const getComponentEquivalentInOtherDocument = (
	compId: string,
	from: {
		structure: Structure
		data: Data
		widgetCompId: string
	},
	to: {
		structure: Structure
		widgetCompId: string
		connections: Record<string, string>
	}
): Structure['string'] | undefined => {
	if (compId === from.widgetCompId) {
		// shortcut. we know both ids
		return to.structure[to.widgetCompId]
	}
	const controllerId = from.structure[from.widgetCompId].dataQuery!
	const componentRole = getComponentRole(from.structure, from.data, controllerId, compId)
	const connectionQuery = to.connections[componentRole]
	return _.values(to.structure).find((compStructure) => compStructure.connectionQuery === connectionQuery)
}

const _resolveAppWidgetGhosts: AppWidgetsGhostsFunctionLibrary['resolveAppWidgetGhosts'] = (
	widgetId,
	widgetCompId,
	structure,
	data,
	templateWidgetCompId,
	templateStructure,
	templateData,
	requestPageId
) => {
	/* Our structure and data might be missing some parts from the template. We can compare the two using the fact that
		connection roles are kept the same for a controller across all installation.
	*/
	const controllerId = getAppControllerId(structure, widgetCompId)
	const connections = getControllerConnections(data, controllerId)

	// change ghost item ids to prevent id conflicts for siblings of the same AppWidget
	const uniqueId = (id: string) => `${widgetCompId}_wgt_${id}`

	const templateControllerId = getAppControllerId(templateStructure, templateWidgetCompId)
	const templateConnections = getControllerConnections(templateData, templateControllerId)

	const missingConnectionIds = _.values(_.omit(templateConnections, _.keys(connections)))
	const missingConnectionsData = _(templateData.connections_data)
		.pick(missingConnectionIds)
		.mapKeys((connectionData, id) => uniqueId(id))
		.mapValues((connectionData, id) => {
			const items = connectionData.items
				// pick only relevant items
				.filter((item) => (item as ControllerConnectionItem).controllerId === templateControllerId)
				// set controller id to the id found in the page instead of the template
				.map((item) => ({ ...item, controllerId }))
			return { ...connectionData, id, items }
		})
		.value()

	// if we find any missing elements they will have to be added to their new parent's components array
	// the parent is already in the structure so it will be easier to merge if this diff is kept separately
	const existingStructureChanges = {}
	const extraProperties: Data['component_properties'] = {}
	const missingStructure: Structure = _.assign(
		{},
		...missingConnectionIds.map((connectionId) => {
			const connectionStructure = getStructureForConnection(templateStructure, connectionId)
			const structureUpdates: Partial<Structure['string']> = {}

			if (!connectionStructure.propertyQuery) {
				// component without property data item, add default item so we can mark it as ghost
				const ghostPropertyQuery = `${connectionStructure.id}_ghost_props`
				structureUpdates.propertyQuery = ghostPropertyQuery
				extraProperties[ghostPropertyQuery] = {
					id: ghostPropertyQuery,
					type: 'DefaultProperties',
					// RefComponent is marked based on COLLAPSED value
					// @ts-ignore
					ghost: connectionStructure.componentType.endsWith('RefComponent')
						? 'COLLAPSED'
						: APP_WIDGET_GHOST_VALUE,
				}
			}

			// fix metadata
			if (connectionStructure.metaData?.pageId) {
				structureUpdates.metaData = {
					...connectionStructure.metaData,
					pageId: requestPageId,
				}
			}

			// fix parent - convert from compId in template to equivalent parent in local
			const parent = getComponentEquivalentInOtherDocument(
				connectionStructure.parent!,
				{
					structure: templateStructure,
					data: templateData,
					widgetCompId: templateWidgetCompId,
				},
				{
					structure,
					widgetCompId,
					connections,
				}
			)

			if (parent) {
				// parent is in the page structure, need change the one declared in the template
				structureUpdates.parent = parent.id

				// add new child into parent components
				_.set(
					existingStructureChanges,
					[parent.id, 'components'],
					[
						..._.get(
							existingStructureChanges,
							[parent.id, 'components'],
							_.get(structure, [parent.id, 'components'], [])
						),
						uniqueId(connectionStructure.id),
					]
				)
			} else {
				// parent is also a ghost, fix its id to be unique id
				structureUpdates.parent = uniqueId(connectionStructure.parent!)
			}

			return { [connectionStructure.id]: { ...connectionStructure, ...structureUpdates } }
		})
	)

	const missingDocumentDataItemIds = _(missingStructure).values().map('dataQuery').compact().value()
	const missingDocumentData: Data['document_data'] = _(templateData.document_data)
		.pick(missingDocumentDataItemIds)
		.mapKeys((item, id) => uniqueId(id))
		.mapValues((item, id) => ({ ...item, id }))
		.value()
	const missingPropIds = _(missingStructure).values().map('propertyQuery').compact().value()
	const missingProps = _(templateData.component_properties)
		.pick(missingPropIds)
		.assign(extraProperties)
		.mapKeys((item, id) => uniqueId(id))
		// mark missing elements with as ghosts so other modules can distinguish them from regular structure
		// @ts-ignore
		.mapValues((props, id) => ({ ...props, id, ghost: props.ghost || APP_WIDGET_GHOST_VALUE }))
		.value()
	const missingStyleIds = _(missingStructure).values().map('styleId').compact().value()
	const missingStyles = _(templateData.theme_data)
		.pick(missingStyleIds)
		.mapKeys((item, id) => uniqueId(id))
		.mapValues((item, id) => ({ ...item, id }))
		.value()

	const ghostStructure: Structure = _(missingStructure)
		.mapKeys((compStructure, id) => uniqueId(id))
		.mapValues((compStructure, id) => {
			const { styleId, dataQuery, propertyQuery, connectionQuery, parent, components } = compStructure
			const structureIdUpdates = {
				id,
				styleId: styleId ? uniqueId(styleId) : null,
				dataQuery: dataQuery ? uniqueId(dataQuery) : null,
				propertyQuery: propertyQuery ? uniqueId(propertyQuery) : null,
				connectionQuery: connectionQuery ? uniqueId(connectionQuery) : null,
				parent: parent ? uniqueId(parent) : null,
				components: components ? components.map(uniqueId) : null,
			}
			return { ...compStructure, ..._.pickBy(structureIdUpdates) }
		})
		.value()

	return {
		data: {
			document_data: missingDocumentData,
			connections_data: missingConnectionsData,
			component_properties: missingProps,
			theme_data: missingStyles,
		},
		structure: {
			...ghostStructure,
			...existingStructureChanges,
		},
	}
}

export const createAppWidgetGhostFunctionLibrary = (
	logger: Logger,
	metaSiteId: string
): AppWidgetsGhostsFunctionLibrary => {
	const resolveAppWidgetGhosts: AppWidgetsGhostsFunctionLibrary['resolveAppWidgetGhosts'] = (widgetId, ...args) => {
		try {
			return _resolveAppWidgetGhosts(widgetId, ...args)
		} catch (error) {
			const wrappedError = new Error(
				`Error resolving AppWidget ghosts for widget ${widgetId} on metaSiteId ${metaSiteId}: ${error?.message}`
			)
			logger.error(wrappedError)
			return {
				data: {
					document_data: {},
					connections_data: {},
					component_properties: {},
					theme_data: {},
				},
				structure: {},
			}
		}
	}

	return {
		wrapTemplateWithAppWidget,
		resolveAppWidgetGhosts,
		// @ts-ignore
		buildWidgetUrl,
	}
}
